/* Cart offers use Shopify's existing products and automatic discounts. */
if (!customElements.get('rv-cart-offers')) {
  class RollProofCartOffers extends HTMLElement {
    static busy = false;

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-action]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.applyOffer(button);
      });
    }

    async readCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error(this.dataset.errorMessage);
      return response.json();
    }

    sections() {
      const sections = [];
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const node = document.getElementById(id);
        if (node?.dataset.id) sections.push({ id, section: node.dataset.id, selector: `#${id}` });
      }
      if (document.querySelector('cart-drawer')) sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' });
      if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
      return sections;
    }

    async refresh(sections, message) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.map((part) => part.section).join(','));
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('refresh');
      const rendered = await response.json();
      const replacements = sections.map((part) => {
        const documentFragment = new DOMParser().parseFromString(rendered[part.section] || '', 'text/html');
        return { current: document.getElementById(part.id), next: documentFragment.querySelector(part.selector) };
      });
      if (replacements.some((part) => !part.current || !part.next)) throw new Error('refresh');
      for (const part of replacements) part.current.innerHTML = part.next.innerHTML;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        if (drawer.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('#CartDrawer'), drawer.querySelector('.drawer__close'));
      }
      const status = document.querySelector('#main-cart-items [data-cart-offer-status]') || document.querySelector('cart-drawer.active [data-cart-offer-status]');
      if (status) {
        status.textContent = message;
        status.setAttribute('tabindex', '-1');
        status.focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('rollproof:cart-updated'));
    }

    async applyOffer(button) {
      if (RollProofCartOffers.busy || document.querySelector('.cart__items--disabled')) return;
      const data = { ...this.dataset };
      const sections = this.sections();
      const status = this.querySelector('[data-cart-offer-status]');
      const message = data.updatedMessage || 'Your cart has been updated.';
      const pendingInput = [...document.querySelectorAll('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')].some((input) => input.value !== input.getAttribute('value'));
      if (pendingInput) {
        if (status) status.textContent = data.changedMessage || 'Please let the quantity update finish, then choose your offer.';
        return;
      }
      const controls = [...document.querySelectorAll('rv-cart-offers button, cart-items button, cart-items input, cart-drawer-items button, cart-drawer-items input, #checkout, #CartDrawer-Checkout')];
      const initialDisabled = controls.map((control) => [control, control.disabled]);
      const preventEdit = (event) => {
        if (event.target.closest('cart-remove-button, quantity-input, #checkout, #CartDrawer-Checkout, .cart__dynamic-checkout-buttons, .additional-checkout-buttons')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      RollProofCartOffers.busy = true;
      document.documentElement.classList.add('rv-cart-updating');
      document.addEventListener('click', preventEdit, true);
      controls.forEach((control) => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = data.busyMessage || 'Updating your cart…';
      let mutationStarted = false;
      let mutationAccepted = false;
      const eventPromises = [];
      try {
        const cart = await this.readCart();
        const doorId = Number(data.doorId);
        const trunkId = Number(data.trunkId);
        const pairId = Number(data.pairId);
        const doors = cart.items.filter((item) => item.variant_id === doorId);
        const doorCount = doors.reduce((sum, item) => sum + item.quantity, 0);
        if (cart.currency !== 'USD' || cart.total_price !== Number(data.cartTotal) || doorCount !== Number(data.doorCount)) {
          await this.refresh(sections, data.changedMessage || 'Your cart changed. Please choose your offer again.');
          return;
        }

        let path = 'cart/add.js';
        let payload;
        let targetId;
        let expectedPrice;
        let productHandle;
        let expectedQuantity;
        if (button.dataset.action === 'add-door') {
          const quantity = Number(button.dataset.quantity);
          if (![1, 2].includes(quantity) || !doorId) throw new Error(data.errorMessage);
          payload = { items: [{ id: doorId, quantity }] };
          targetId = doorId;
          expectedPrice = 9900;
          productHandle = 'door-sill-protector';
          expectedQuantity = doorCount + quantity;
        } else if (button.dataset.action === 'add-trunk') {
          if (!trunkId) throw new Error(data.errorMessage);
          payload = { items: [{ id: trunkId, quantity: 1 }] };
          targetId = trunkId;
          expectedPrice = 12900;
          productHandle = 'trunk-sill-protector';
          expectedQuantity = cart.items.filter((item) => item.variant_id === trunkId).reduce((sum, item) => sum + item.quantity, 0) + 1;
        } else if (button.dataset.action === 'upgrade-pair') {
          const door = doors[0];
          const properties = Object.values(door?.properties || {}).filter((value) => value !== '' && value !== null);
          if (doorCount !== 1 || doors.length !== 1 || properties.length || door.selling_plan_allocation || !pairId || cart.items.some((item) => [pairId, trunkId].includes(item.variant_id))) {
            await this.refresh(sections, data.changedMessage || 'Your cart changed. Please choose your offer again.');
            return;
          }
          // A single Shopify update replaces the original line, preserving all other lines and attributes.
          path = 'cart/update.js';
          payload = { updates: { [door.key]: 0, [pairId]: 1 } };
          targetId = pairId;
          expectedPrice = 19900;
          productHandle = 'the-rollproof-pair';
          expectedQuantity = 1;
        } else {
          throw new Error(data.errorMessage);
        }

        const productResponse = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`, { cache: 'no-store' });
        if (!productResponse.ok) throw new Error(data.errorMessage);
        const product = await productResponse.json();
        const variant = product.variants.find((item) => item.id === targetId);
        if (!variant?.available || variant.price !== expectedPrice) {
          await this.refresh(sections, data.changedMessage || 'This offer has changed. Please review your cart.');
          return;
        }

        const StandardCartEvent = window.StandardEvents?.CartLinesUpdateEvent;
        if (StandardCartEvent) {
          const operations = [{ action: 'add', lines: [{ merchandiseId: String(targetId), quantity: payload.items?.[0]?.quantity || 1 }] }];
          if (button.dataset.action === 'upgrade-pair') operations.unshift({ action: 'remove', lines: [{ id: doors[0].key, quantity: 0 }] });
          for (const operation of operations) {
            const deferred = StandardCartEvent.createPromise();
            deferred.promise.catch(() => {});
            eventPromises.push(deferred);
            this.dispatchEvent(new StandardCartEvent({ ...operation, context: 'cart', promise: deferred.promise }));
          }
        }
        mutationStarted = true;
        const response = await fetch(`${window.Shopify.routes.root}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok || result.status >= 400 || result.errors) {
          throw new Error(result.description || data.errorMessage);
        }
        mutationAccepted = true;
        const updated = await this.readCart();
        eventPromises.forEach((deferred) => deferred.resolve({ cart: window.StandardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(updated) }));
        const targetQuantity = updated.items.filter((item) => item.variant_id === targetId).reduce((sum, item) => sum + item.quantity, 0);
        if (targetQuantity !== expectedQuantity || (button.dataset.action === 'upgrade-pair' && updated.items.some((item) => item.variant_id === doorId))) throw new Error('Cart changed during update');
        await this.refresh(sections, message);
      } catch (error) {
        eventPromises.forEach((deferred) => deferred.reject(error));
        if (mutationAccepted || mutationStarted) {
          // Never retry an uncertain cart write: reconcile Shopify's authoritative cart instead.
          try { await this.refresh(sections, data.errorMessage || 'Please review your current cart before trying again.'); }
          catch { window.location.reload(); }
          return;
        }
        if (status) status.textContent = error.message || data.errorMessage || 'Unable to update your cart. Please try again.';
      } finally {
        RollProofCartOffers.busy = false;
        document.documentElement.classList.remove('rv-cart-updating');
        document.removeEventListener('click', preventEdit, true);
        initialDisabled.forEach(([control, wasDisabled]) => { if (control.isConnected) control.disabled = wasDisabled; });
        if (this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('rv-cart-offers', RollProofCartOffers);
}
